import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthProvider";

type Item = {
  id: string;
  code: string;
  value: string;
  short_name?: string | null;
  sort_order: number;
  is_active: boolean;
  color?: string | null; // HEX #RRGGBB
};

type Column = "code" | "value" | "short_name" | "sort_order" | "is_active" | "color";

interface Props {
  title: string;
  listUrl: string;                    // GET (returnează toate, active + inactive)
  createUrl: string;                  // POST
  updateUrl: (code: string) => string; // PATCH (field-level)
  deleteUrl?: (code: string) => string; // nefolosit (toggle-ul face PATCH)
  columns?: Column[];                 // ce coloane să afişăm
}

/** Normalizează orice input la HEX #RRGGBB (majuscule). Returnează null dacă e invalid. */
const normalizeHex = (raw: string): string | null => {
  if (!raw) return null;
  let s = raw.trim().replace(/^#/, "").toUpperCase();
  if (s.length === 3) {
    s = s.split("").map((c) => c + c).join("");
  }
  const ok = /^[0-9A-F]{6}$/.test(s);
  return ok ? `#${s}` : null;
};

const GenericTaxonomyManagerColor: React.FC<Props> = ({
  title,
  listUrl,
  createUrl,
  updateUrl,
  deleteUrl, // păstrat pentru compatibilitate, nu este folosit
  columns = ["code", "value", "short_name", "sort_order", "is_active", "color"],
}) => {
  const { token } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showInactive, setShowInactive] = useState<boolean>(() => {
    return localStorage.getItem(`showInactive_${title}`) === "true";
  });

  const [form, setForm] = useState<{
    code: string;
    value: string;
    short_name: string;
    sort_order: number;
    is_active: boolean;
    color: string;
  }>({
    code: "",
    value: "",
    short_name: "",
    sort_order: 0,
    is_active: true,
    color: "#CFE2FF", // default vizibil
  });

  useEffect(() => {
    localStorage.setItem(`showInactive_${title}`, String(showInactive));
  }, [showInactive, title]);

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    timer: 1800,
    showConfirmButton: false,
  });

  const normalizeItem = (raw: any, idx: number): Item => {
    const code = (raw?.code ?? raw?.key ?? "").toString();
    const value = (raw?.value ?? raw?.name ?? "").toString();
    const short_name = raw?.short_name ?? raw?.shortName ?? null;
    const sort_order = Number(raw?.sort_order ?? raw?.priority ?? 0);
    const is_active = raw?.is_active ?? raw?.active ?? true;
    // Acceptă raw.color, color_hex etc.
    const colorRaw = (raw?.color ?? raw?.colour ?? raw?.color_hex ?? null) as string | null;
    const color = colorRaw ? normalizeHex(colorRaw) : null;
    const id = (raw?.id ?? code ?? raw?.key ?? String(idx)).toString();
    return { id, code, value, short_name, sort_order, is_active, color };
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(listUrl, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
        ? data.items
        : null;

      if (!arr) throw new Error("Răspuns invalid (nu e listă).");

      const normalized: Item[] = arr.map((row: any, i: number) => normalizeItem(row, i));
      // sortare implicită: sort_order ASC, apoi value ASC
      normalized.sort(
        (a, b) => a.sort_order - b.sort_order || a.value.localeCompare(b.value, "ro", { sensitivity: "base" })
      );
      setItems(normalized);
    } catch (e: any) {
      console.error(e);
      Swal.fire("Eroare", e.message || "Nu s-a putut încărca lista.", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchItems();
  }, [token]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code || !form.value) {
      Toast.fire({ icon: "error", title: "Completează key și value" });
      return;
    }
    const hex = normalizeHex(form.color) || "#CFE2FF";
    try {
      const payload: any = {
        code: form.code.trim(),
        value: form.value.trim(),
        short_name: form.short_name?.trim() || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: !!form.is_active,
        color: hex,
      };
      const r = await fetch(createUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const { detail } = await r.json().catch(() => ({}));
        throw new Error(detail || "Eroare la creare.");
      }
      setShowAdd(false);
      setForm({ code: "", value: "", short_name: "", sort_order: 0, is_active: true, color: "#CFE2FF" });
      Toast.fire({ icon: "success", title: "Creat!" });
      fetchItems();
    } catch (err: any) {
      Swal.fire("Eroare", err.message || "Eroare la creare.", "error");
    }
  };

  const onUpdateField = async (code: string, field: keyof Item, value: any) => {
    try {
      if (field === "code") return; // cheia nu se modifică
      const payload: any = {
        [field]:
          field === "sort_order"
            ? Number(value)
            : field === "color"
            ? normalizeHex(value) || null
            : value,
      };

      // Optimistic update
      setItems((prev) => prev.map((i) => (i.code === code ? { ...i, ...payload } : i)));

      const r = await fetch(updateUrl(code), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const { detail } = await r.json().catch(() => ({}));
        throw new Error(detail || "Eroare la actualizare.");
      }
      Toast.fire({ icon: "success", title: "Salvat!" });
    } catch (err: any) {
      Swal.fire("Eroare", err.message || "Eroare la actualizare.", "error");
      fetchItems(); // revenire la date corecte
    }
  };

  const visibleItems = useMemo(
    () => (showInactive ? items : items.filter((i) => i.is_active)),
    [items, showInactive]
  );

  return (
    <div className="box-card">
      <div className="box-head">
        <h2 className="page-subtitle">{title}</h2>
        <div className="box-actions">
          <div className="toggle-switch">
            
                <input
                id="toggleSwitch"
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                />
            <label htmlFor="toggleSwitch">
                <span className="toggle-slider"></span>
            </label>
            <span className="toggle-label">{showInactive ? "Arată inactivi" : "Arată activi"}</span>
            </div>
          <button className="primary-btn" onClick={() => setShowAdd(true)}>
            Adaugă
          </button>
        </div>
      </div>

      {showAdd && (
        <form className="form-row grid-2 add-setting-form" onSubmit={onCreate}>
          {columns.includes("code") && (
            <div>
              <label>Key</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="ex: kinetoterapie"
                required
              />
            </div>
          )}
          {columns.includes("value") && (
            <div>
              <label>Denumire</label>
              <input
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="ex: Kinetoterapie"
                required
              />
            </div>
          )}
          {columns.includes("short_name") && (
            <div>
              <label>Prescurtare</label>
              <input
                value={form.short_name}
                onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                placeholder="ex: KIN"
              />
            </div>
          )}
          {columns.includes("sort_order") && (
            <div>
              <label>Nr. ordine</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
              />
            </div>
          )}
          {columns.includes("is_active") && (
            <div className="checkbox-row">
              <label>Activ</label>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            </div>
          )}

          {columns.includes("color") && (
            <div className="row-color">
              <label>Culoare</label>
              <div className="row-color-items" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="color"
                  value={normalizeHex(form.color) || "#CFE2FF"}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
                <input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  onBlur={(e) => {
                    const normalized = normalizeHex(e.target.value);
                    if (!normalized) {
                      Swal.fire("Eroare", "Introduce o culoare HEX validă (#RRGGBB sau #RGB).", "error");
                      setForm((prev) => ({
                        ...prev,
                        color: normalizeHex(prev.color) || "#CFE2FF",
                      }));
                    } else {
                      setForm((prev) => ({ ...prev, color: normalized }));
                    }
                  }}
                  placeholder="#RRGGBB"
                  style={{ width: 110, textTransform: "uppercase" }}
                />
                <span
                  title="Previzualizare"
                  style={{
                    display: "inline-block",
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: normalizeHex(form.color) || "#CFE2FF",
                  }}
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button className="cancel-btn" onClick={() => setShowAdd(false)} type="button">
              Anulează
            </button>
            <button className="primary-btn" type="submit">
              Salvează
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Se încarcă…</p>
      ) : visibleItems.length === 0 ? (
        <p>Nu există elemente.</p>
      ) : (
        <div className="table-rounded-container">
          <table className="table-rounded settings-table">
            <thead>
              <tr>
                {columns.includes("code") && <th>Key</th>}
                {columns.includes("value") && <th>Denumire</th>}
                {columns.includes("short_name") && <th>Prescurtare</th>}
                {columns.includes("sort_order") && <th>Prioritate</th>}
                {columns.includes("is_active") && <th>Activ</th>}
                {columns.includes("color") && <th>Culoare</th>}
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((it) => (
                <tr key={it.id} className={!it.is_active ? "row-inactive" : undefined}>
                  {columns.includes("code") && <td><code>{it.code}</code></td>}

                  {columns.includes("value") && (
                    <td>
                      <input
                        value={it.value}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((p) => (p.id === it.id ? { ...p, value: e.target.value } : p))
                          )
                        }
                        onBlur={(e) => onUpdateField(it.code, "value", e.target.value)}
                      />
                    </td>
                  )}

                  {columns.includes("short_name") && (
                    <td>
                      <input
                        value={it.short_name ?? ""}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((p) => (p.id === it.id ? { ...p, short_name: e.target.value } : p))
                          )
                        }
                        onBlur={(e) => onUpdateField(it.code, "short_name", e.target.value || null)}
                      />
                    </td>
                  )}

                  {columns.includes("sort_order") && (
                    <td>
                      <input
                        type="number"
                        value={it.sort_order}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((p) =>
                              p.id === it.id ? { ...p, sort_order: Number(e.target.value) } : p
                            )
                          )
                        }
                        onBlur={(e) => onUpdateField(it.code, "sort_order", Number(e.target.value))}
                        style={{ width: 80 }}
                      />
                    </td>
                  )}

                  {columns.includes("is_active") && (
                    <td>
                      <button
                        className={it.is_active ? "active" : "inactive"}
                        onClick={() => onUpdateField(it.code, "is_active", !it.is_active)}
                        title="Activează/Dezactivează"
                      >
                        {it.is_active ? "Activ" : "Inactiv"} <span className="button-dot">•</span>
                      </button>
                    </td>
                  )}

                  {columns.includes("color") && (
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input
                          type="color"
                          value={normalizeHex(it.color || "") || "#FFFFFF"}
                          onChange={(e) => {
                            const v = e.target.value;
                            setItems((prev) =>
                              prev.map((p) => (p.id === it.id ? { ...p, color: v } : p))
                            );
                          }}
                          onBlur={(e) =>
                            onUpdateField(it.code, "color", normalizeHex(e.target.value) || "#FFFFFF")
                          }
                        />
                        <input
                          value={it.color || ""}
                          onChange={(e) => {
                            const v = e.target.value.toUpperCase();
                            setItems((prev) =>
                              prev.map((p) => (p.id === it.id ? { ...p, color: v } : p))
                            );
                          }}
                          onBlur={(e) => {
                            const normalized = normalizeHex(e.target.value);
                            if (!normalized) {
                              Swal.fire("Eroare", "Culoare HEX invalidă (#RRGGBB sau #RGB).", "error");
                              setItems((prev) =>
                                prev.map((p) =>
                                  p.id === it.id
                                    ? { ...p, color: normalizeHex(p.color || "") || "#FFFFFF" }
                                    : p
                                )
                              );
                            } else {
                              onUpdateField(it.code, "color", normalized);
                              setItems((prev) =>
                                prev.map((p) => (p.id === it.id ? { ...p, color: normalized } : p))
                              );
                            }
                          }}
                          placeholder="#RRGGBB"
                          style={{ width: 110, textTransform: "uppercase" }}
                        />
                        <span
                          title="Previzualizare"
                          style={{
                            display: "inline-block",
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            border: "1px solid #ccc",
                            background: normalizeHex(it.color || "") || "#FFFFFF",
                          }}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .row-inactive { opacity: .6; }
        .box-head .box-actions { display: flex; gap: 12px; align-items: center; }
        .toggle-inactive input { vertical-align: middle; }
        .button-dot { margin-left: 6px; }
        .table-rounded td input[type="color"] { width: 42px; height: 32px; padding: 0; border: none; background: transparent; }
      `}</style>
    </div>
  );
};

export default GenericTaxonomyManagerColor;